import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Pulse, Warning, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * ExtensionHealthPanel · iter 305 round 6
 *
 * Founder-only dashboard panel showing live "selector health" of the
 * Pronto Publisher extension. Pulls from GET /api/admin/extension-health.
 *
 * Used inside /dashboard/admin to detect within hours when a portal
 * redesigns and our selectors break (otherwise we'd wait for an angry
 * agent to email us).
 */
export default function ExtensionHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/extension-health");
        setData(data);
      } catch {
        // founder-only check failure or no data — just hide
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshTick]);

  if (loading) return null;
  if (!data) return null;

  const o = data.overall || {};
  const portals = Object.entries(data.by_portal || {});
  const worst = data.by_field_worst || [];
  const failures = data.recent_failures || [];
  const versions = data.versions || {};

  // Empty state — no telemetry yet
  if (o.total_events === 0) {
    return (
      <div className="bg-white border border-[#E5E7E0] p-5 mb-8" data-testid="ext-health-empty">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] mb-2 font-semibold">
          <Pulse size={14} /> Salute estensione Pronto Publisher
        </div>
        <div className="text-[13px] text-[#6B6A65] leading-relaxed">
          Nessun evento telemetria ancora. Quando il primo agente userà l'estensione
          su un portale, qui vedrai in tempo reale quanti campi vengono compilati
          correttamente e quali selettori si stanno rompendo.
        </div>
      </div>
    );
  }

  const successRateColor = (rate) => {
    if (rate === null || rate === undefined) return "#9A968B";
    if (rate >= 90) return "#7C8B6E";
    if (rate >= 70) return "#C49A2A";
    return "#C44A2A";
  };

  return (
    <div className="mb-8" data-testid="ext-health-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold flex items-center gap-2">
          <Pulse size={14} /> Salute estensione · ultimi 7 giorni
        </h2>
        <button
          onClick={() => setRefreshTick((t) => t + 1)}
          className="text-[11px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1.5"
          data-testid="ext-health-refresh"
        >
          <ArrowsClockwise size={12} /> Aggiorna
        </button>
      </div>

      {/* Overall stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="ext-health-overall">
        <div className="bg-white border border-[#E5E7E0] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Tasso successo</div>
          <div
            className="font-editorial text-3xl mt-1 tracking-tight"
            style={{ color: successRateColor(o.success_rate) }}
            data-testid="ext-health-success-rate"
          >
            {o.success_rate !== null ? `${o.success_rate}%` : "—"}
          </div>
          <div className="text-[10.5px] text-[#9A968B] mt-1">{o.total_fills} compilazioni</div>
        </div>
        <div className="bg-white border border-[#E5E7E0] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Eventi totali</div>
          <div className="font-editorial text-3xl mt-1 tracking-tight text-[#1C1C1A]">{o.total_events}</div>
          <div className="text-[10.5px] text-[#9A968B] mt-1">7 giorni</div>
        </div>
        <div className="bg-white border border-[#E5E7E0] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Agenti attivi 7g</div>
          <div className="font-editorial text-3xl mt-1 tracking-tight text-[#1C1C1A]">{o.unique_users_7d}</div>
          <div className="text-[10.5px] text-[#9A968B] mt-1">{o.unique_users_24h} oggi</div>
        </div>
        <div className="bg-white border border-[#E5E7E0] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Versioni attive</div>
          <div className="font-editorial text-base mt-1.5 text-[#1C1C1A] leading-tight">
            {Object.entries(versions).map(([v, n]) => (
              <div key={v} className="flex items-center justify-between gap-2 text-[12px]">
                <span className="font-mono">{v}</span>
                <span className="text-[#9A968B]">{n}</span>
              </div>
            ))}
            {Object.keys(versions).length === 0 && <span className="text-[#9A968B] text-[12px]">—</span>}
          </div>
        </div>
      </div>

      {/* Per portal */}
      {portals.length > 0 && (
        <div className="bg-white border border-[#E5E7E0] mb-5 overflow-x-auto" data-testid="ext-health-portals">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F4EE] text-[10px] uppercase tracking-[0.16em] text-[#6B6A65]">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Portale</th>
                <th className="px-4 py-2.5 text-right font-semibold">Eventi</th>
                <th className="px-4 py-2.5 text-right font-semibold">OK</th>
                <th className="px-4 py-2.5 text-right font-semibold">Fail</th>
                <th className="px-4 py-2.5 text-right font-semibold">Tasso</th>
                <th className="px-4 py-2.5 text-right font-semibold">Ultimo evento</th>
              </tr>
            </thead>
            <tbody>
              {portals.map(([p, data]) => (
                <tr key={p} className="border-t border-[#EAE7DF] text-[13px]" data-testid={`ext-health-portal-${p}`}>
                  <td className="px-4 py-3 font-semibold text-[#1C1C1A]">{p}</td>
                  <td className="px-4 py-3 text-right text-[#6B6A65]">{data.events}</td>
                  <td className="px-4 py-3 text-right text-[#7C8B6E]">{data.success}</td>
                  <td className="px-4 py-3 text-right text-[#C44A2A]">{data.fail}</td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className="font-semibold"
                      style={{ color: successRateColor(data.success_rate) }}
                    >
                      {data.success_rate !== null ? `${data.success_rate}%` : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-[10.5px] text-[#9A968B]">
                    {data.last_event ? data.last_event.slice(0, 19).replace("T", " ") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Worst fields */}
      {worst.length > 0 && (
        <div className="bg-white border border-[#E5E7E0] p-5 mb-5" data-testid="ext-health-worst">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#C44A2A] font-semibold mb-3">
            <Warning size={14} weight="fill" /> Selettori da sistemare (worst first)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {worst.slice(0, 10).map((f, i) => (
              <div
                key={`${f.portal}-${f.field}-${i}`}
                className="flex items-center justify-between gap-3 px-3 py-2 border border-[#EAE7DF]"
                data-testid={`worst-field-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-[#1C1C1A] truncate">
                    {f.portal} · {f.field}
                  </div>
                  <div className="text-[10.5px] text-[#9A968B]">
                    {f.success} ok · {f.fail} fail
                  </div>
                </div>
                <span
                  className="font-editorial text-xl tracking-tight"
                  style={{ color: successRateColor(f.success_rate) }}
                >
                  {f.success_rate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent failures */}
      {failures.length > 0 && (
        <details className="bg-white border border-[#E5E7E0]" data-testid="ext-health-failures">
          <summary className="cursor-pointer p-4 text-[12px] font-semibold text-[#1C1C1A] flex items-center gap-2 group">
            <Warning size={12} className="text-[#C44A2A]" />
            Ultimi {failures.length} fallimenti (clicca per espandere)
            <span className="ml-auto text-[#9A968B] group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="border-t border-[#EAE7DF] max-h-96 overflow-y-auto">
            {failures.map((f, i) => (
              <div
                key={i}
                className="px-4 py-2.5 border-b border-[#F5F4EE] text-[12px] flex items-start gap-3"
              >
                <span className="text-[10.5px] text-[#9A968B] font-mono whitespace-nowrap pt-0.5">
                  {f.timestamp?.slice(11, 19) || "—"}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-[#1C1C1A]">{f.portal}</span>
                  <span className="text-[#6B6A65]"> · {f.field}</span>
                  {f.step && <span className="text-[#9A968B]"> · {f.step}</span>}
                  <span className="text-[#C44A2A] ml-2">{f.error || "selector not matched"}</span>
                </div>
                <span className="text-[10.5px] text-[#9A968B] truncate max-w-[140px]">
                  {f.user_email} · v{f.version}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
